import React, { PointerEvent, useCallback, useMemo, useRef } from "react";

import type { OrgEmployee } from "../types";

import { cx, getDepartmentPalette } from "../utils";

import css from "../OrgChart.module.scss";
import { LayoutNode, NODE_HEIGHT, NODE_WIDTH } from "../layout";
import type { Transform } from "../useViewport";

export function Minimap({
	nodes,
	edges,
	employeeMap,
	transform,
	viewportWidth,
	viewportHeight,
	onNavigate,
	highlightedNodeId,
	selectedNodeId,
}: {
	nodes: LayoutNode[];
	edges: Array<{ source: string; target: string }>;
	employeeMap: Map<string, OrgEmployee>;
	transform: Transform;
	viewportWidth: number;
	viewportHeight: number;
	onNavigate: (contentX: number, contentY: number) => void;
	highlightedNodeId: string | null;
	selectedNodeId: string | null;
}) {
	const minimapRef = useRef<HTMLDivElement>(null);
	const previewWidth = 220;
	const previewHeight = 146;

	const { bounds, scale } = useMemo(() => {
		if (nodes.length === 0) {
			return {
				bounds: { minX: 0, minY: 0, maxX: 1, maxY: 1 },
				scale: 1,
			};
		}

		let minX = Infinity;
		let minY = Infinity;
		let maxX = -Infinity;
		let maxY = -Infinity;
		for (const node of nodes) {
			minX = Math.min(minX, node.x);
			minY = Math.min(minY, node.y);
			maxX = Math.max(maxX, node.x + node.width);
			maxY = Math.max(maxY, node.y + node.height);
		}

		const padX = Math.max((maxX - minX) * 0.1, 20);
		const padY = Math.max((maxY - minY) * 0.1, 20);
		const paddedBounds = {
			minX: minX - padX,
			minY: minY - padY,
			maxX: maxX + padX,
			maxY: maxY + padY,
		};
		const contentWidth = paddedBounds.maxX - paddedBounds.minX;
		const contentHeight = paddedBounds.maxY - paddedBounds.minY;
		return {
			bounds: paddedBounds,
			scale: Math.min(previewWidth / contentWidth, previewHeight / contentHeight),
		};
	}, [nodes]);

	const nodeById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
	const viewportRect = useMemo(() => {
		const visibleLeft = -transform.x / transform.scale;
		const visibleTop = -transform.y / transform.scale;
		const visibleWidth = viewportWidth / transform.scale;
		const visibleHeight = viewportHeight / transform.scale;
		return {
			x: (visibleLeft - bounds.minX) * scale,
			y: (visibleTop - bounds.minY) * scale,
			width: visibleWidth * scale,
			height: visibleHeight * scale,
		};
	}, [bounds.minX, bounds.minY, scale, transform, viewportHeight, viewportWidth]);

	const handlePointer = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			event.currentTarget.setPointerCapture(event.pointerId);
			event.stopPropagation();
			const rect = minimapRef.current?.getBoundingClientRect();
			if (!rect) return;
			const contentX = (event.clientX - rect.left) / scale + bounds.minX;
			const contentY = (event.clientY - rect.top) / scale + bounds.minY;
			onNavigate(contentX, contentY);
		},
		[bounds.minX, bounds.minY, onNavigate, scale]
	);

	if (nodes.length === 0) return null;

	return (
		<div data-interactive className={css.minimapPanel}>
			<div
				ref={minimapRef}
				className={css.minimapPreview}
				style={{ width: previewWidth, height: previewHeight }}
				onPointerDown={handlePointer}
				onPointerMove={(event) => {
					if (event.buttons === 1) handlePointer(event);
				}}
			>
				<svg className={css.minimapEdges} width={previewWidth} height={previewHeight}>
					{edges.map(({ source, target }) => {
						const sourceNode = nodeById.get(source);
						const targetNode = nodeById.get(target);
						if (!sourceNode || !targetNode) return null;
						return (
							<line
								key={`${source}-${target}`}
								x1={(sourceNode.x + sourceNode.width / 2 - bounds.minX) * scale}
								y1={(sourceNode.y + sourceNode.height / 2 - bounds.minY) * scale}
								x2={(targetNode.x + targetNode.width / 2 - bounds.minX) * scale}
								y2={(targetNode.y + targetNode.height / 2 - bounds.minY) * scale}
							/>
						);
					})}
				</svg>
				{nodes.map((node) => {
					const employee = employeeMap.get(node.id);
					const palette = getDepartmentPalette(employee?.department || employee?.name || node.id);
					const isHighlighted = node.id === highlightedNodeId;
					const isSelected = !isHighlighted && node.id === selectedNodeId;
					return (
						<div
							key={node.id}
							className={cx(
								css.minimapNode,
								isHighlighted && css.minimapNodeHighlighted,
								isSelected && css.minimapNodeSelected
							)}
							style={{
								left: (node.x - bounds.minX) * scale,
								top: (node.y - bounds.minY) * scale,
								width: Math.max(isHighlighted ? 7 : isSelected ? 5 : 2, NODE_WIDTH * scale),
								height: Math.max(isHighlighted ? 7 : isSelected ? 5 : 2, NODE_HEIGHT * scale),
								backgroundColor: palette.dot,
							}}
						/>
					);
				})}
				<div className={css.minimapViewport} style={viewportRect} />
			</div>
		</div>
	);
}
